import * as React from 'react';
import { createRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import type { StatutoryKind } from '@bits/shared/zod';
import { rootRoute } from './__root';
import { AppShell } from '@/components/AppShell';
import { api } from '@/lib/api';
import { errorMessage as toMessage } from '@/lib/errors';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Field } from '@/components/ui/field';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';

export const statutoryConfigRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/statutory-config',
  component: StatutoryConfigPage,
});

type AddValues = { kind: StatutoryKind; jurisdiction: string; validFrom: string; paramsJson: string };

function StatutoryConfigPage(): JSX.Element {
  const queryClient = useQueryClient();
  const [verifyOpen, setVerifyOpen] = React.useState<string | null>(null);
  const [reference, setReference] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);

  const rowsQuery = useQuery({
    queryKey: ['statutory-config'],
    queryFn: () => api.listStatutoryConfig(),
  });

  const bootstrap = useMutation({
    mutationFn: () => api.bootstrapStatutoryConfig(),
    onSuccess: async () => {
      setError(null);
      await queryClient.invalidateQueries({ queryKey: ['statutory-config'] });
    },
    onError: (err) => setError(toMessage(err)),
  });

  const verify = useMutation({
    mutationFn: (payload: { id: string; reference: string }) =>
      api.verifyStatutoryConfig(payload.id, { reference: payload.reference }),
    onSuccess: async () => {
      setVerifyOpen(null);
      setReference('');
      setError(null);
      await queryClient.invalidateQueries({ queryKey: ['statutory-config'] });
    },
    onError: (err) => setError(toMessage(err)),
  });

  const [addOpen, setAddOpen] = React.useState(false);
  const addForm = useForm<AddValues>({
    defaultValues: { kind: 'pf', jurisdiction: 'IN', validFrom: '', paramsJson: '{\n  \n}' },
  });
  const create = useMutation({
    mutationFn: (v: AddValues) =>
      api.createStatutoryConfig({
        kind: v.kind,
        jurisdiction: v.jurisdiction.trim().toUpperCase(),
        validFrom: v.validFrom,
        params: JSON.parse(v.paramsJson) as Record<string, unknown>,
      }),
    onSuccess: async () => {
      setAddOpen(false);
      setError(null);
      addForm.reset();
      await queryClient.invalidateQueries({ queryKey: ['statutory-config'] });
    },
    onError: (err) => setError(toMessage(err)),
  });

  const rows = rowsQuery.data ?? [];
  const anyUnverified = rows.some((r) => r.verifiedBy === null);

  return (
    <AppShell>
      <div className="flex flex-wrap items-start justify-between gap-4 lg:flex-nowrap">
        <div className="flex min-w-0 flex-col gap-2">
          <h1 className="font-display text-4xl font-semibold tracking-tight md:text-5xl">
            Statutory config
          </h1>
          <p className="text-base text-[hsl(var(--muted-foreground))]">
            PF, ESI, PT, TDS parameters. Payroll refuses to run against unverified rows.
          </p>
        </div>
        <div className="flex flex-wrap gap-2 lg:shrink-0">
          <Dialog open={addOpen} onOpenChange={setAddOpen}>
            <DialogTrigger asChild>
              <Button>New row</Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>New statutory row</DialogTitle>
                <DialogDescription>
                  Effective-dated: the new row applies from its valid-from date; earlier months
                  keep using the older row. Lands unverified until a CA signs it off.
                </DialogDescription>
              </DialogHeader>
              <form
                onSubmit={addForm.handleSubmit((v) => create.mutate(v))}
                className="flex flex-col gap-3"
              >
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                  <Field label="Kind" required>
                    <Select {...addForm.register('kind')}>
                      <option value="pf">PF</option>
                      <option value="esi">ESI</option>
                      <option value="pt">PT (state)</option>
                      <option value="tds">TDS</option>
                    </Select>
                  </Field>
                  <Field
                    label="Jurisdiction"
                    required
                    hint="IN for central; state code (e.g. TN, KA) for PT"
                    error={addForm.formState.errors.jurisdiction?.message}
                  >
                    <Input
                      className="uppercase"
                      {...addForm.register('jurisdiction', { required: 'Enter a jurisdiction' })}
                    />
                  </Field>
                  <Field label="Valid from" required error={addForm.formState.errors.validFrom?.message}>
                    <Input type="date" {...addForm.register('validFrom', { required: 'Pick a date' })} />
                  </Field>
                </div>
                <Field
                  label="Parameters (JSON)"
                  required
                  hint="Copy an existing row's parameters as a starting point. Money in paise, rates in basis points of a percent (1200 = 12%)."
                  error={addForm.formState.errors.paramsJson?.message}
                >
                  <Textarea
                    rows={10}
                    className="font-mono text-xs"
                    {...addForm.register('paramsJson', {
                      required: 'Enter the parameters',
                      validate: (v) => {
                        try {
                          const parsed: unknown = JSON.parse(v);
                          return (
                            (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) ||
                            'Must be a JSON object'
                          );
                        } catch {
                          return 'Not valid JSON';
                        }
                      },
                    })}
                  />
                </Field>
                {error ? (
                  <Alert variant="destructive">
                    <AlertTitle>Create failed</AlertTitle>
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                ) : null}
                <DialogFooter>
                  <Button type="submit" disabled={create.isPending}>
                    {create.isPending ? 'Saving…' : 'Create row'}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
          <Button
            variant="outline"
            onClick={() => bootstrap.mutate()}
            disabled={bootstrap.isPending}
          >
            {bootstrap.isPending ? 'Seeding…' : 'Bootstrap defaults'}
          </Button>
        </div>
      </div>

      {error ? (
        <Alert variant="destructive">
          <AlertTitle>Action failed</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : null}

      {anyUnverified ? (
        <Alert variant="destructive">
          <AlertTitle>Verification required</AlertTitle>
          <AlertDescription>
            One or more statutory rows are unverified. Payroll runs will be blocked until every
            row is signed off by a CA.
          </AlertDescription>
        </Alert>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>All rows</CardTitle>
          <CardDescription>{rows.length} row(s), newest per (kind, jurisdiction) first</CardDescription>
        </CardHeader>
        <CardContent>
          {rows.length === 0 ? (
            <p className="text-sm text-[hsl(var(--muted-foreground))]">
              No rows yet — click "Bootstrap defaults" to seed India FY 2025-26 values (unverified).
            </p>
          ) : (
            <ul className="flex flex-col divide-y divide-[hsl(var(--border))]">
              {rows.map((r) => (
                <li
                  key={r.id}
                  className="flex flex-col gap-3 py-3 text-sm sm:flex-row sm:items-start sm:justify-between"
                >
                  <div className="flex min-w-0 flex-col gap-1">
                    <div className="flex flex-wrap items-center gap-2 font-medium">
                      {r.kind.toUpperCase()} · {r.jurisdiction}
                      <Badge variant={r.verifiedBy ? 'default' : 'destructive'}>
                        {r.verifiedBy ? `Verified by ${r.verifiedBy}` : 'Unverified'}
                      </Badge>
                    </div>
                    <div className="text-xs text-[hsl(var(--muted-foreground))]">
                      Effective from {r.validFrom}
                    </div>
                    <pre className="mt-1 max-w-2xl overflow-x-auto rounded bg-[hsl(var(--muted))] p-2 text-[11px] leading-tight">
                      {JSON.stringify(r.params, null, 2)}
                    </pre>
                  </div>
                  {!r.verifiedBy ? (
                    <Dialog
                      open={verifyOpen === r.id}
                      onOpenChange={(open) => setVerifyOpen(open ? r.id : null)}
                    >
                      <DialogTrigger asChild>
                        <Button size="sm">Verify</Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Verify {r.kind.toUpperCase()} / {r.jurisdiction}</DialogTitle>
                          <DialogDescription>
                            Recorded to the audit log. Cite the source (circular, notice, or CA
                            reference) so the assertion is traceable.
                          </DialogDescription>
                        </DialogHeader>
                        <div className="flex flex-col gap-1.5">
                          <Label>Reference</Label>
                          <Input
                            value={reference}
                            onChange={(e) => setReference(e.currentTarget.value)}
                            placeholder="e.g. EPFO circular 27/2025 or CA email dated 2026-08-01"
                          />
                        </div>
                        <DialogFooter>
                          <Button
                            onClick={() => verify.mutate({ id: r.id, reference: reference.trim() })}
                            disabled={reference.trim().length === 0 || verify.isPending}
                          >
                            {verify.isPending ? 'Verifying…' : 'Verify'}
                          </Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </AppShell>
  );
}
